import { observer } from "mobx-react";
import React from "react";
import { useInjection } from "inversify-react";
import { AppController } from "@dropDesk/presentation/app/app.controller";
import SelectDateAndTime from "@dropDesk/presentation/components/dates/select_date_and_time";
import DropDeskIcon from "@dropDesk/storage/icons";
import { ICONS_DROPDESK } from "@dropDesk/storage/icons/icons.d";
import { hexToRGBA } from "@dropDesk/utils/helpers/colors";
import {
    formatPeriodDurationLabel,
    getCurrentDate,
    setMaxHour
} from "@dropDesk/utils/helpers/date_helper";
import {
    COMPARATIVE_PRESET_LABELS,
    ComparativePeriodRange,
    ComparativePresetEnum
} from "@dropDesk/utils/helpers/comparative_period.helper";
import {
    ComparativeDateErrors
} from "@dropDesk/presentation/pages/reports/report_ticket_init/report_ticket_comparative/controller/report_ticket_comparative.controller";
import {
    ContentFilter,
    DurationDivider,
    DurationRow,
    DurationText,
    FilterSection,
    IconCircle,
    PeriodBadge,
    PeriodBlock,
    PeriodHeader,
    PeriodInputWrapper,
    PeriodInputsRow,
    PeriodSeparator,
    PeriodsRow,
    PeriodTitle,
    PresetButton,
    PresetsContent,
    PresetsLabel,
} from "./styled";

const PRESET_OPTIONS: ComparativePresetEnum[] = [
    ComparativePresetEnum.todayVsYesterday,
    ComparativePresetEnum.currentWeekVsPrevious,
    ComparativePresetEnum.currentMonthVsPrevious,
    ComparativePresetEnum.morningVsAfternoon,
];

const SearchBar = observer(({
    period,
    comparePeriod,
    preset,
    dateErrors,
    setPeriodStart,
    setPeriodEnd,
    setComparePeriodStart,
    setComparePeriodEnd,
    applyPreset,
}: {
    period: ComparativePeriodRange;
    comparePeriod: ComparativePeriodRange;
    preset: ComparativePresetEnum | null;
    dateErrors: ComparativeDateErrors;
    setPeriodStart: (date: Date | null) => void;
    setPeriodEnd: (date: Date | null) => void;
    setComparePeriodStart: (date: Date | null) => void;
    setComparePeriodEnd: (date: Date | null) => void;
    applyPreset: (preset: ComparativePresetEnum) => void;
}) => {
    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;

    const accentSoft = hexToRGBA(colors.accent, 0.12);
    const accentGradient = hexToRGBA(colors.accent, 0.08);
    const graySoft = hexToRGBA(colors.text, 0.08);
    const softDivider = hexToRGBA(colors.border, 0.7);
    const presetsBackground = hexToRGBA(colors.text, 0.03);

    const preventSelectDateLaterToday = (current: Date): boolean => {
        return current.getTime() > setMaxHour(getCurrentDate()).getTime();
    };

    const periodDuration = formatPeriodDurationLabel(period[0], period[1]);
    const compareDuration = formatPeriodDurationLabel(comparePeriod[0], comparePeriod[1]);

    return (
        <ContentFilter background={colors.onBackground}>
            <FilterSection border={colors.border} background={colors.onBackground}>
                <PeriodsRow gradientstart={accentGradient}>
                    <PeriodBlock>
                        <PeriodHeader>
                            <IconCircle background={accentSoft}>
                                <DropDeskIcon icon={ICONS_DROPDESK.calendar} size={15} color={colors.accent} />
                            </IconCircle>
                            <PeriodTitle color={colors.text}>Período atual</PeriodTitle>
                            <PeriodBadge color={colors.accent} background={accentSoft}>Atual</PeriodBadge>
                        </PeriodHeader>
                        <PeriodInputsRow>
                            <PeriodInputWrapper iconcolor={colors.accent}>
                                <SelectDateAndTime
                                    size={37}
                                    showSeconds={true}
                                    hideMessageError={true}
                                    allowClear={true}
                                    messageError={dateErrors.periodStart}
                                    value={period[0]}
                                    placeHolder="Início"
                                    disabledDate={preventSelectDateLaterToday}
                                    onChange={setPeriodStart}
                                />
                            </PeriodInputWrapper>
                            <PeriodSeparator color={colors.hint}>até</PeriodSeparator>
                            <PeriodInputWrapper iconcolor={colors.accent}>
                                <SelectDateAndTime
                                    size={37}
                                    showSeconds={true}
                                    hideMessageError={true}
                                    allowClear={true}
                                    messageError={dateErrors.periodEnd}
                                    value={period[1]}
                                    placeHolder="Fim"
                                    disabledDate={preventSelectDateLaterToday}
                                    onChange={setPeriodEnd}
                                />
                            </PeriodInputWrapper>
                        </PeriodInputsRow>
                        {!!periodDuration && (
                            <>
                                <DurationDivider border={softDivider} />
                                <DurationRow>
                                    <DropDeskIcon icon={ICONS_DROPDESK.clock} size={13} color={colors.accent} />
                                    <DurationText color={colors.accent}>{periodDuration}</DurationText>
                                </DurationRow>
                            </>
                        )}
                    </PeriodBlock>

                    <PeriodBlock>
                        <PeriodHeader>
                            <IconCircle background={graySoft}>
                                <DropDeskIcon icon={ICONS_DROPDESK.calendar} size={15} color={colors.text} />
                            </IconCircle>
                            <PeriodTitle color={colors.text}>Comparar com</PeriodTitle>
                            <PeriodBadge color={colors.text} background={graySoft}>Comparação</PeriodBadge>
                        </PeriodHeader>
                        <PeriodInputsRow>
                            <PeriodInputWrapper iconcolor={colors.text}>
                                <SelectDateAndTime
                                    size={37}
                                    showSeconds={true}
                                    hideMessageError={true}
                                    allowClear={true}
                                    messageError={dateErrors.compareStart}
                                    value={comparePeriod[0]}
                                    placeHolder="Início"
                                    disabledDate={preventSelectDateLaterToday}
                                    onChange={setComparePeriodStart}
                                />
                            </PeriodInputWrapper>
                            <PeriodSeparator color={colors.hint}>até</PeriodSeparator>
                            <PeriodInputWrapper iconcolor={colors.text}>
                                <SelectDateAndTime
                                    size={37}
                                    showSeconds={true}
                                    hideMessageError={true}
                                    allowClear={true}
                                    messageError={dateErrors.compareEnd}
                                    value={comparePeriod[1]}
                                    placeHolder="Fim"
                                    disabledDate={preventSelectDateLaterToday}
                                    onChange={setComparePeriodEnd}
                                />
                            </PeriodInputWrapper>
                        </PeriodInputsRow>
                        {!!compareDuration && (
                            <>
                                <DurationDivider border={softDivider} />
                                <DurationRow>
                                    <DropDeskIcon icon={ICONS_DROPDESK.clock} size={13} color={colors.hint} />
                                    <DurationText color={colors.hint}>{compareDuration}</DurationText>
                                </DurationRow>
                            </>
                        )}
                    </PeriodBlock>
                </PeriodsRow>
            </FilterSection>

            <PresetsContent border={'transparent'} background={'transparent'}>
                <PresetsLabel color={colors.text}>Atalhos rápidos</PresetsLabel>
                {PRESET_OPTIONS.map((presetOption) => {
                    const isActive = preset === presetOption;
                    return (
                        <PresetButton
                            key={presetOption}
                            type="button"
                            active={isActive}
                            color={colors.text}
                            border={colors.border}
                            accent={colors.accent}
                            background={colors.onBackground}
                            onClick={() => applyPreset(presetOption)}
                        >
                            <DropDeskIcon
                                icon={ICONS_DROPDESK.calendar}
                                size={14}
                                color={isActive ? '#FFFFFF' : colors.text}
                            />
                            {COMPARATIVE_PRESET_LABELS[presetOption]}
                        </PresetButton>
                    );
                })}
            </PresetsContent>
        </ContentFilter>
    );
});

export default SearchBar;
